import { ApiResponse } from 'apisauce';
import { ErrorInfo } from '@/shared/interfaces/error-info.interface';
import { UserAuthModel } from '@/shared/models/user-auth.model';
import { UserAuthUIModel } from '@/shared/ui-models/user-auth.ui-model';
import {
  cleanupUserData,
  refreshUserToken
} from '@/shared/services/helpers/auth-helper/auth-helper.service';
import { userApi } from '@/shared/services/apis/user-api.service';
import UserAuthMapper from '@/shared/mappers/user-auth.mapper';
import errorHelper from '@/shared/services/helpers/error-helper/error-helper.service';
import { setUserProperties } from '@/shared/services/utils/tracking/tracking.service';
import { AuthStatus } from '@/shared/enums/auth.status.enum';
import { AddUserDataModel } from '@/shared/models/user.model';
import { restoreLanguageForSession } from '@/shared/services/user-preference-sync.service';

/**
 * function to check use token validity
 */
export function checkTokenValidity(): Promise<boolean> {
  return userApi
    .validateToken()
    .then(
      (response) => {
        if (response.status === 202) {
          return true;
        } else {
          cleanupUserData();
          return false;
        }
      },
      (rejected) => {
        console.error(rejected);
        cleanupUserData();
        return false;
      }
    )
    .catch((error) => {
      console.error(error);
      cleanupUserData();
      return false;
    });
}

/**
 * function to check use token validity
 */
export function refreshUserSession(): Promise<AuthStatus> {
  const userData = refreshUserToken();
  if (userData === null) {
    cleanupUserData();
    return new Promise((resolve) => resolve(AuthStatus.UNAUTHENTICATED));
  } else {
    return userApi
      .validateToken()
      .then(
        (response) => {
          if (response.status === 202) {
            setUserProperties(
              userData.userId,
              userData.email,
              userData.role?.toString() || 'guest'
            );
            void restoreLanguageForSession();
            return AuthStatus.AUTHENTICATED;
          } else if (response.status === 403) {
            return AuthStatus.UNAUTHORIZED;
          } else if (response.status === 401) {
            cleanupUserData();
            return AuthStatus.UNAUTHENTICATED;
          } else {
            return AuthStatus.UNKNOWN;
          }
        },
        (rejected) => {
          console.error('rejected>>>>', rejected);

          cleanupUserData();
          return AuthStatus.UNAUTHENTICATED;
        }
      )
      .catch((error) => {
        console.error(error);
        cleanupUserData();
        return AuthStatus.UNAUTHENTICATED;
      });
  }
}

/**
 * function to sign up user
 * @param email {string} user email
 * @param password {string} user password
 * @param containerName {string} UI container
 */
export function registerUser(
  user: AddUserDataModel,
  containerName: string
): Promise<UserAuthUIModel | ErrorInfo> {
  return userApi
    .registerUser(user)
    .then(async (response: ApiResponse<UserAuthModel, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data) {
          const mappedData = UserAuthMapper.mapToUI(data);
          setUserProperties(
            mappedData.userId,
            mappedData.email,
            mappedData.role?.toString() || 'guest'
          );
          return mappedData;
        } else {
          const error: ErrorInfo = errorHelper(
            0,
            '',
            containerName,
            (userApi.registerUser as any).name
          );
          return error;
        }
      } else {
        const error: ErrorInfo = errorHelper(
          Number(response.status),
          response.data && response.data.errorCode
            ? String(response.data.errorCode || '')
            : response.problem,
          containerName,
          (userApi.registerUser as any).name
        );

        return error;
      }
    });
}

/**
 * function to login user
 * @param email {string} user email
 * @param password {string} user password
 * @param containerName {string} UI container
 */
export function loginUser(
  email: string,
  password: string,
  containerName: string,
  captchaToken?: string
): Promise<UserAuthUIModel | ErrorInfo> {
  return userApi
    .loginUser(email, password, captchaToken)
    .then(async (response: ApiResponse<UserAuthModel, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data) {
          const mappedData = UserAuthMapper.mapToUI(data);
          setUserProperties(
            mappedData.userId,
            mappedData.email,
            mappedData.role?.toString() || 'guest'
          );
          return mappedData;
        } else {
          const error: ErrorInfo = errorHelper(
            0,
            '',
            containerName,
            (userApi.loginUser as any).displayName
          );
          return error;
        }
      } else {
        const rawData = response.data as
          | (ErrorInfo & { message?: string; nextAvailableAt?: string })
          | undefined;
        const error: ErrorInfo = errorHelper(
          Number(response.status),
          rawData && rawData.errorCode ? String(rawData.errorCode || '') : response.problem,
          containerName,
          (userApi.loginUser as any).displayName
        );
        if (rawData?.message) {
          error.serverMessage = rawData.message;
        }
        if (rawData?.nextAvailableAt) {
          error.nextAvailableAt = rawData.nextAvailableAt;
        }
        return error;
      }
    });
}

export function userRefreshToken(
  refreshToken: string,
  containerName?: string
): Promise<UserAuthUIModel | ErrorInfo> {
  return userApi
    .refreshToken(refreshToken)
    .then((response: ApiResponse<UserAuthModel, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data) {
          const mappedData = UserAuthMapper.mapToUI(data);
          setUserProperties(
            mappedData.userId,
            mappedData.email,
            mappedData.role?.toString() || 'guest'
          );
          return mappedData;
        } else {
          const error: ErrorInfo = errorHelper(
            0,
            '',
            containerName || 'DASHBOARD_MODULE',
            (userApi.refreshToken as any).displayName
          );
          return error;
        }
      } else {
        const error: ErrorInfo = errorHelper(
          Number(response.status),
          response.data && response.data.errorCode
            ? String(response.data.errorCode || '')
            : response.problem,
          containerName || 'DASHBOARD_MODULE',
          (userApi.refreshToken as any).displayName
        );

        return error;
      }
    });
}

/**
 * function to change user password
 * @param token {string} server side generated token
 * @param password {string} user new password
 * @param containerName {string} UI container
 */
export function changeUserPassword(
  token: string,
  password: string,
  containerName: string,
  captchaToken?: string
): Promise<UserAuthUIModel | ErrorInfo> {
  return userApi
    .changePassword(token, password, captchaToken)
    .then((response: ApiResponse<UserAuthModel, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data) {
          const mappedData = UserAuthMapper.mapToUI(data);
          setUserProperties(
            mappedData.userId,
            mappedData.email,
            mappedData.role?.toString() || 'guest'
          );
          return mappedData;
        } else {
          const error: ErrorInfo = errorHelper(
            0,
            '',
            containerName,
            (userApi.loginUser as any).displayName
          );
          return error;
        }
      } else {
        const error: ErrorInfo = errorHelper(
          Number(response.status),
          response.data && response.data.errorCode
            ? String(response.data.errorCode || '')
            : response.problem,
          containerName,
          (userApi.loginUser as any).displayName
        );

        return error;
      }
    });
}

/**
 * function to forgot user password
 * @param email {string} user email
 * @param containerName {string} UI container
 */
export function forgotUserPassword(
  email: string,
  containerName: string,
  captchaToken?: string
): Promise<unknown | ErrorInfo> {
  return userApi
    .forgotPassword(email, captchaToken)
    .then((response: ApiResponse<unknown, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data) {
          return data;
        } else {
          const error: ErrorInfo = errorHelper(
            0,
            '',
            containerName,
            (userApi.loginUser as any).displayName
          );
          return error;
        }
      } else {
        const error: ErrorInfo = errorHelper(
          Number(response.status),
          response.data && response.data.errorCode
            ? String(response.data.errorCode || '')
            : response.problem,
          containerName,
          (userApi.loginUser as any).displayName
        );

        return error;
      }
    });
}
